/**
 * Générateur — Attendu 957 — « Calculer l'aire d'un triangle. » (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Triangles
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/957.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Formule de référence, écrite partout sous la même forme en trois temps :
 *  aire = base × hauteur relative à cette base ÷ 2 (`formule_de_reference`).
 *  L'unité fait partie de la réponse : elle est saisie par l'élève, jamais
 *  imprimée à côté du champ (`unite_de_la_reponse`).
 *
 *  1. formule       — niveau 1 : base et hauteur données par le texte, sans
 *                     figure (saisie de l'aire ; phrase à trous en trois temps :
 *                     le produit, la division, le résultat).
 *  2. directe       — niveau 2 : figure, triangle sans angle obtus, pied de la
 *                     hauteur intérieur au côté ; quatre longueurs cotées, deux
 *                     seulement servent (saisie + phrase à trous).
 *  3. obtus         — niveau 3 : figure, triangle obtusangle, pied de la hauteur
 *                     sur le prolongement du côté, prolongement tracé en trait
 *                     fin (saisie + QCM d'identification de la hauteur).
 *  4. rectangle     — niveau 4 : triangle rectangle, les deux côtés de l'angle
 *                     droit jouent base et hauteur ; l'hypoténuse est la
 *                     longueur inutile (saisie).
 *  5. deux_hauteurs — niveau 5 : deux hauteurs tracées et codées, une seule
 *                     cotée ; l'élève choisit le couple base-hauteur cohérent
 *                     (saisie + QCM « quel calcul donne l'aire ? »).
 *  Niveau 6 (mélange) : `generate()` sans clé.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - base : entier de 3 à 10 cm ; hauteur relative : entier de 3 à 9 cm ;
 *   - parité du produit base × hauteur : un tirage sur deux donne un produit
 *     impair, donc une aire en demi-entier, sur les cartes qui le permettent
 *     (voir Non tranché) ;
 *   - position du pied de la hauteur : intérieur (niveaux 2 et 5), extérieur sur
 *     le prolongement (niveau 3), confondu avec un sommet (niveau 4, exception
 *     prévue par l'exclusion 2) ;
 *   - distance du pied aux sommets : au moins 0,6 cm quand il est intérieur ;
 *   - écart entre la hauteur et un côté issu du même sommet : au moins 0,2 cm
 *     sur les longueurs affichées ;
 *   - inclinaison de la base : entier de -60 à 60 degrés, jamais 0 (exclusion 3) ;
 *   - quatre longueurs cotées sur chaque figure, toutes entières, chacune
 *     conforme à la figure dessinée au dixième près (vigilance 2), sauf le
 *     niveau 4 (voir Non tranché).
 *  Exclusions : aucune hauteur de même longueur affichée qu'un côté issu du même
 *   sommet ; aucun pied confondu avec un sommet hors du niveau 4 ; aucune base
 *   horizontale ni verticale ; aucune donnée manquante (quatre longueurs, deux
 *   suffisent) ; aucune conversion d'unité dans le calcul (toutes les longueurs
 *   sont en centimètres) ; aucune aire demandée sans que la hauteur relative
 *   soit tracée et codée.
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. multiplier deux côtés au lieu de la base par la hauteur relative
 *      → `deux_cotes_multiplies` (deux_hauteurs_qcm_unique)
 *   2. oublier la division par deux (aire du rectangle associé)
 *      → `oubli_division_par_deux` (deux_hauteurs_qcm_unique ; provoquée par la
 *        parité du produit sur toutes les saisies)
 *   3. prendre pour hauteur le segment reliant un sommet à une extrémité du
 *      côté opposé → `cote_pris_pour_hauteur` (obtus_qcm_unique)
 *   4. croire qu'une hauteur coupe nécessairement le côté opposé
 *      → `hauteur_toujours_interieure` (obtus_qcm_unique ; `erreurVisee` de
 *        obtus_texte)
 *   5. identifier la hauteur comme le trait vertical de la figure
 *      → `hauteur_verticale` (obtus_qcm_unique ; toutes les bases sont
 *        inclinées, aucune hauteur n'est verticale)
 *   6. associer une hauteur à un côté dont elle n'est pas la hauteur relative
 *      → `hauteur_mal_associee` (deux_hauteurs_qcm_unique ; `erreurVisee` de
 *        deux_hauteurs_texte)
 *   7. rendre l'aire en centimètres au lieu de centimètres carrés, ou sans unité
 *      → `unite_de_longueur` (`erreurVisee` de toutes les saisies : l'unité est
 *        saisie, jamais imprimée)
 *   8. pour un triangle rectangle, croire qu'il existe une formule différente
 *      → `formule_speciale_rectangle` (`erreurVisee` de rectangle_texte)
 *
 *  Graduation → cartes : 1 formule · 2 directe · 3 obtus · 4 rectangle ·
 *   5 deux_hauteurs · 6 `generate()` sans clé.
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - `parite_du_produit_base_hauteur` et `nature_des_longueurs_distractrices`
 *     se contredisent sur les cartes à figure : exiger que les deux côtés
 *     inutiles soient entiers ET conformes au dessin au dixième près ne laisse
 *     que 31 triangles à pied intérieur, dont 6 à produit impair (recherche
 *     exhaustive). Le tirage force malgré tout un produit impair une fois sur
 *     deux, au prix d'une variété réduite sur cette moitié.
 *   - Niveau 4 : un triangle rectangle n'a que trois côtés et sa hauteur
 *     relative à un côté de l'angle droit EST l'autre côté. Les quatre
 *     longueurs cotées et l'écart hauteur / côté voisin y sont impossibles :
 *     trois longueurs sont affichées, l'hypoténuse étant la seule inutile.
 *   - Niveau 5 : aucun triangle des bornes n'a deux couples base-hauteur tous
 *     deux entiers et distincts (recherche exhaustive). Les deux hauteurs sont
 *     donc tracées et codées, une seule est cotée, et le choix du couple porte
 *     sur le côté où tombe le pied.
 *   - La correction « figure épurée des longueurs inutiles » et l'espace de
 *     calcul en sortie papier relèvent du rendu, non du générateur.
 */

import {
    randInt, randomChoice, pickLettresSains,
    makeSaisie, makeQcm, makeTexteTrou,
} from '../../../utils';
import {
    creerFigure, cadreur, imgElement, traceHauteur, milieu, pied,
} from './svgTriangles';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 957;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_FORMULE = 'Appliquer la formule';
const L_DIRECTE = 'Aire d\'un triangle';
const L_OBTUS = 'Hauteur hors du triangle';
const L_RECT = 'Aire d\'un triangle rectangle';
const L_DEUX_H = 'Choisir le bon couple base-hauteur';

const BASE_MIN = 3;
const BASE_MAX = 10;
const HAUT_MIN = 3;
const HAUT_MAX = 9;

const VB = { w: 400, h: 310 };
const OR = '#b45309';    // hauteur cotée
const VIOLET = '#7c3aed'; // seconde hauteur, non cotée
const ENCRE = '#334155';

const toRad = (d) => (d * Math.PI) / 180;
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWXYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);
const salt = (...n) => n.map((x) => Math.round(x)).join('_');

// Mots-clés des erreurs visées.
const E_DEUX_COTES = 'deux_cotes_multiplies';
const E_OUBLI_DIV = 'oubli_division_par_deux';
const E_COTE_HAUTEUR = 'cote_pris_pour_hauteur';
const E_HAUTEUR_INT = 'hauteur_toujours_interieure';
const E_VERTICALE = 'hauteur_verticale';
const E_MAL_ASSOCIEE = 'hauteur_mal_associee';
const E_UNITE = 'unite_de_longueur';
const E_FORMULE_RECT = 'formule_speciale_rectangle';

/** Écriture française d'un entier ou d'un demi-entier. */
const fr = (x) => String(x).replace('.', ',');

/**
 * Les écritures admises d'une aire, pour une saisie en comparaison
 * d'expression : l'unité est exigée (vigilance 5 : `cm2`, `cm²` et
 * « centimètres carrés » sont la même unité), et un demi-entier s'écrit aussi
 * en fraction non réduite (vigilance 4). La première est la forme canonique.
 */
const ecrituresAire = (aire, unite = 'cm') => {
    const n = fr(aire);
    const base = [
        `${n} ${unite}²`, `${n} ${unite}2`, `${n} ${unite}^2`,
        `${n} ${unite === 'cm' ? 'centimètres carrés' : 'mètres carrés'}`,
        `${n} ${unite === 'cm' ? 'centimetres carres' : 'metres carres'}`,
    ];
    if (!Number.isInteger(aire)) {
        const num = Math.round(aire * 2);
        base.push(`${num}/2 ${unite}²`, `${num}/2 ${unite}2`, `${num}/2 ${unite}^2`);
    }
    return base;
};

const saisieAire = (aire, texte, label, extra = {}) => makeSaisie(ecrituresAire(aire)[0], texte, {
    ...opts(label),
    strict: true,
    comparaison: 'expression',
    reponses_acceptees: ecrituresAire(aire).slice(1),
    ...extra,
});

/** Rappel d'unité, commun à toutes les saisies d'aire (`unite_de_la_reponse`). */
const RAPPEL_UNITE = ' <em>Une unité est attendue dans la réponse.</em>';

// ═══════════════════════════════════════════════════════════
// VIVIERS DE FIGURES
// ═══════════════════════════════════════════════════════════

/**
 * Triangles à pied de hauteur INTÉRIEUR (niveaux 2 et 5). `p` est la distance
 * du pied au sommet B le long de [BC]. `ab` et `ac` sont les deux longueurs
 * inutiles : entières (`nature_des_longueurs_distractrices`) et conformes au
 * dessin au dixième près (vigilance 2). Recherche exhaustive sur b ∈ [3 ; 10],
 * h ∈ [3 ; 9], pied à plus de 0,6 cm de chaque sommet, côtés ≤ 14 cm, écart
 * d'au moins 0,2 cm entre la hauteur et chacun des deux côtés issus de A.
 */
const BRUT_INTERIEUR = [
    { b: 6, h: 4, p: 2.8523, ab: 5, ac: 5 },
    { b: 7, h: 6, p: 3.4276, ab: 7, ac: 7 },
    { b: 8, h: 3, p: 2.7002, ab: 4, ac: 6 },
    { b: 8, h: 3, p: 3.8881, ab: 5, ac: 5 },
    { b: 8, h: 3, p: 5.2201, ab: 6, ac: 4 },
    { b: 8, h: 5, p: 3.1509, ab: 6, ac: 7 },
    { b: 8, h: 5, p: 4.7695, ab: 7, ac: 6 },
    { b: 8, h: 7, p: 3.9445, ab: 8, ac: 8 },
    { b: 8, h: 8, p: 3.9228, ab: 9, ac: 9 },
    { b: 9, h: 3, p: 2.576, ab: 4, ac: 7 },
    { b: 9, h: 3, p: 3.8869, ab: 5, ac: 6 },
    { b: 9, h: 3, p: 5.092, ab: 6, ac: 5 },
    { b: 9, h: 3, p: 6.2248, ab: 7, ac: 4 },
    { b: 9, h: 4, p: 3.1462, ab: 5, ac: 7 },
    { b: 9, h: 4, p: 4.4078, ab: 6, ac: 6 },
    { b: 9, h: 4, p: 5.8524, ab: 7, ac: 5 },
    { b: 9, h: 6, p: 3.5735, ab: 7, ac: 8 },
    { b: 9, h: 6, p: 5.2228, ab: 8, ac: 7 },
    { b: 9, h: 9, p: 4.4385, ab: 10, ac: 10 },
    { b: 10, h: 3, p: 2.5077, ab: 4, ac: 8 },
    { b: 10, h: 3, p: 7.3191, ab: 8, ac: 4 },
    { b: 10, h: 4, p: 2.9681, ab: 5, ac: 8 },
    { b: 10, h: 4, p: 4.3507, ab: 6, ac: 7 },
    { b: 10, h: 4, p: 5.6346, ab: 7, ac: 6 },
    { b: 10, h: 4, p: 6.8524, ab: 8, ac: 5 },
    { b: 10, h: 5, p: 4.9733, ab: 7, ac: 7 },
    { b: 10, h: 8, p: 3.9229, ab: 9, ac: 10 },
    { b: 10, h: 8, p: 5.8489, ab: 10, ac: 9 },
];

/**
 * Triangles OBTUSANGLES, pied de la hauteur sur le prolongement de [BC]
 * (niveau 3) : `p` est hors de [0 ; b], à plus de 0,6 cm du sommet le plus
 * proche et à moins d'une longueur de base au-delà. Mêmes contraintes sur les
 * deux longueurs inutiles.
 */
const BRUT_EXTERIEUR = [
    { b: 3, h: 4, p: -2.8538, ab: 5, ac: 7 },
    { b: 3, h: 4, p: 5.8475, ab: 7, ac: 5 },
    { b: 4, h: 4, p: -3.0319, ab: 5, ac: 8 },
    { b: 4, h: 4, p: 6.8476, ab: 8, ac: 5 },
    { b: 4, h: 5, p: -3.4767, ab: 6, ac: 9 },
    { b: 4, h: 5, p: 7.3749, ab: 9, ac: 6 },
    { b: 5, h: 3, p: -2.5132, ab: 4, ac: 8 },
    { b: 5, h: 3, p: 7.5077, ab: 8, ac: 4 },
    { b: 5, h: 4, p: -3.1477, ab: 5, ac: 9 },
    { b: 5, h: 4, p: 7.9617, ab: 9, ac: 5 },
    { b: 5, h: 5, p: -4.8988, ab: 7, ac: 11 },
    { b: 5, h: 5, p: 9.7695, ab: 11, ac: 7 },
    { b: 5, h: 8, p: -4.0645, ab: 9, ac: 12 },
    { b: 5, h: 8, p: 8.9228, ab: 12, ac: 9 },
    { b: 5, h: 9, p: -4.505, ab: 10, ac: 13 },
    { b: 5, h: 9, p: 9.2558, ab: 13, ac: 10 },
    { b: 6, h: 3, p: -2.5806, ab: 4, ac: 9 },
    { b: 6, h: 3, p: 8.5078, ab: 9, ac: 4 },
    { b: 6, h: 4, p: -3.1476, ab: 5, ac: 10 },
    { b: 6, h: 4, p: 9.067, ab: 10, ac: 5 },
    { b: 6, h: 5, p: -5.0076, ab: 7, ac: 12 },
    { b: 6, h: 5, p: 10.8098, ab: 12, ac: 7 },
    { b: 6, h: 7, p: -3.8572, ab: 8, ac: 12 },
    { b: 6, h: 7, p: 9.6836, ab: 12, ac: 8 },
    { b: 6, h: 8, p: -4.316, ab: 9, ac: 13 },
    { b: 6, h: 8, p: 10.1326, ab: 13, ac: 9 },
    { b: 7, h: 3, p: -2.6336, ab: 4, ac: 10 },
    { b: 7, h: 3, p: 9.5076, ab: 10, ac: 4 },
    { b: 7, h: 4, p: -4.4091, ab: 6, ac: 12 },
    { b: 7, h: 4, p: 11.3507, ab: 12, ac: 6 },
    { b: 7, h: 5, p: -6.1728, ab: 8, ac: 14 },
    { b: 7, h: 5, p: -5.0267, ab: 7, ac: 13 },
    { b: 7, h: 5, p: 11.9026, ab: 13, ac: 7 },
    { b: 7, h: 5, p: 13.1294, ab: 14, ac: 8 },
    { b: 7, h: 6, p: -6.8283, ab: 9, ac: 15 },
    { b: 7, h: 6, p: -3.496, ab: 7, ac: 12 },
    { b: 7, h: 6, p: 10.4277, ab: 12, ac: 7 },
    { b: 7, h: 6, p: 13.6495, ab: 15, ac: 9 },
    { b: 7, h: 7, p: -4.0556, ab: 8, ac: 13 },
    { b: 7, h: 7, p: 10.8477, ab: 13, ac: 8 },
    { b: 8, h: 3, p: -2.6763, ab: 4, ac: 11 },
    { b: 8, h: 3, p: 10.5077, ab: 11, ac: 4 },
    { b: 8, h: 4, p: -4.4637, ab: 6, ac: 13 },
    { b: 8, h: 4, p: 12.3507, ab: 13, ac: 6 },
    { b: 8, h: 5, p: -6.2373, ab: 8, ac: 15 },
    { b: 8, h: 5, p: -5.0267, ab: 7, ac: 14 },
    { b: 8, h: 5, p: 12.9805, ab: 14, ac: 7 },
    { b: 8, h: 5, p: 14.1293, ab: 15, ac: 8 },
    { b: 8, h: 6, p: -3.6339, ab: 7, ac: 13 },
    { b: 8, h: 6, p: 11.4312, ab: 13, ac: 7 },
    { b: 8, h: 7, p: -4.0555, ab: 8, ac: 14 },
    { b: 8, h: 7, p: 12.0205, ab: 14, ac: 8 },
    { b: 9, h: 3, p: -2.7117, ab: 4, ac: 12 },
    { b: 9, h: 3, p: 11.526, ab: 12, ac: 4 },
    { b: 9, h: 4, p: -4.5102, ab: 6, ac: 14 },
    { b: 9, h: 4, p: 13.3509, ab: 14, ac: 6 },
    { b: 9, h: 6, p: -3.7485, ab: 7, ac: 14 },
    { b: 9, h: 6, p: 12.5496, ab: 14, ac: 7 },
    { b: 10, h: 3, p: -2.7413, ab: 4, ac: 13 },
    { b: 10, h: 3, p: 12.5567, ab: 13, ac: 4 },
    { b: 10, h: 4, p: -4.55, ab: 6, ac: 15 },
    { b: 10, h: 4, p: 14.3637, ab: 15, ac: 6 },
    { b: 10, h: 5, p: -3.173, ab: 6, ac: 14 },
    { b: 10, h: 5, p: 13.151, ab: 14, ac: 6 },
    { b: 10, h: 6, p: -3.777, ab: 7, ac: 15 },
    { b: 10, h: 6, p: 13.6497, ab: 15, ac: 7 },
];

/**
 * Lisibilité des quatre cotes : le nombre porté par la hauteur ne doit être
 * celui d'aucune autre longueur affichée, sans quoi le choix de la donnée
 * devient indécidable (esprit de `ecart_minimal_entre_la_hauteur_et_les_cotes_voisins`,
 * étendu à la base, puisque les quatre nombres sont lus ensemble).
 */
const lisible = (t) => [t.b, t.ab, t.ac].every((x) => Math.abs(x - t.h) >= 0.5);
const VIVIER_INTERIEUR = BRUT_INTERIEUR.filter(lisible);
const VIVIER_EXTERIEUR = BRUT_EXTERIEUR.filter(lisible);

/**
 * Triangles RECTANGLES du niveau 4 : `b` et `h` sont les deux côtés de l'angle
 * droit, `hyp` l'hypoténuse — entière au dixième près, et distincte des deux
 * autres longueurs. Le cas isocèle (b = h) est exclu : la base et la hauteur y
 * porteraient le même nombre.
 */
const VIVIER_RECTANGLE = [
    { b: 3, h: 4, hyp: 5 },
    { b: 4, h: 3, hyp: 5 },
    { b: 4, h: 7, hyp: 8 },
    { b: 4, h: 8, hyp: 9 },
    { b: 6, h: 8, hyp: 10 },
    { b: 7, h: 4, hyp: 8 },
    { b: 8, h: 4, hyp: 9 },
    { b: 8, h: 6, hyp: 10 },
    { b: 8, h: 9, hyp: 12 },
    { b: 9, h: 8, hyp: 12 },
];

const impair = (t) => (t.b * t.h) % 2 === 1;

/**
 * Tirage dans un vivier en forçant la parité : un tirage sur deux donne un
 * produit base × hauteur IMPAIR, donc une aire en demi-entier
 * (`parite_du_produit_base_hauteur`). Repli déterministe si un sous-ensemble
 * est vide (jamais de `throw`).
 */
const tirerVivier = (vivier) => {
    const veutImpair = randInt(0, 1) === 1;
    const sous = vivier.filter((t) => impair(t) === veutImpair);
    return randomChoice(sous.length ? sous : vivier);
};

/** Inclinaison de la base : entier de -60 à 60, jamais 0 (exclusion 3). */
const tirerInclinaison = () => {
    const a = randInt(12, 60);
    return randInt(0, 1) === 0 ? a : -a;
};

// ═══════════════════════════════════════════════════════════
// FIGURE
// ═══════════════════════════════════════════════════════════

const txtAt = (fig, x, y, txt, couleur = ENCRE, fs = 12) =>
    fig.brut(`<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" font-family="sans-serif" font-size="${fs}" fill="${couleur}" text-anchor="middle">${txt}</text>`);

/** Étiquette posée au milieu de [PQ], décalée du côté opposé à `ref`. */
const etiq = (fig, P, Q, ref, txt, couleur = ENCRE) => {
    const m = milieu(P, Q);
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const n = Math.hypot(dx, dy) || 1;
    let px = -dy / n;
    let py = dx / n;
    if ((m.x + px - ref.x) ** 2 + (m.y + py - ref.y) ** 2
        < (m.x - px - ref.x) ** 2 + (m.y - py - ref.y) ** 2) { px = -px; py = -py; }
    txtAt(fig, m.x + 18 * px, m.y + 18 * py + 4, txt, couleur);
};

/**
 * Figure d'un triangle ABC : base [BC] inclinée de `inclin` degrés, sommet A à
 * la distance `h` de la droite (BC), pied de la hauteur à l'abscisse `p` le long
 * de [BC] (hors de [0 ; b] pour le cas obtusangle). La hauteur est tracée en
 * pointillés avec son codage d'angle droit (`objets_geometriques` 1 et 4).
 * `cotes` : les longueurs à afficher. `secondeHauteur` : trace en plus, sans la
 * coter, la hauteur issue de B relative à [AC] (niveau 5).
 */
const figTriangle = ({ b, h, p, inclin, nA, nB, nC, cotes, secondeHauteur, s }) => {
    const ux = Math.cos(toRad(inclin));
    const uy = -Math.sin(toRad(inclin));
    const nx = uy;
    const ny = -ux;
    const B0 = { x: 0, y: 0 };
    const C0 = { x: b * ux, y: b * uy };
    const A0 = { x: p * ux + h * nx, y: p * uy + h * ny };
    const H0 = { x: p * ux, y: p * uy };
    const cad = cadreur([A0, B0, C0, H0], VB, 48);
    const A = cad(A0);
    const B = cad(B0);
    const C = cad(C0);
    const fig = creerFigure(VB, s);
    fig.segment(A, B);
    fig.segment(B, C);
    fig.segment(C, A);
    const G = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 };
    const dir = (P) => (Math.atan2(-(P.y - G.y), P.x - G.x) * 180) / Math.PI;
    fig.point(A, nA, dir(A), 14);
    fig.point(B, nB, dir(B), 14);
    fig.point(C, nC, dir(C), 14);
    const H = traceHauteur(fig, A, B, C, { couleur: OR });
    if (secondeHauteur) traceHauteur(fig, B, A, C, { couleur: VIOLET });
    if (cotes.base) etiq(fig, B, C, G, cotes.base, ENCRE);
    if (cotes.ab) etiq(fig, A, B, G, cotes.ab, ENCRE);
    if (cotes.ac) etiq(fig, A, C, G, cotes.ac, ENCRE);
    if (cotes.hauteur) etiq(fig, A, H, C, cotes.hauteur, OR);
    return fig;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — formule (niveau 1) : sans figure
// ═══════════════════════════════════════════════════════════

const tirerSansFigure = () => {
    const veutImpair = randInt(0, 1) === 1;
    for (let i = 0; i < 80; i++) {
        const b = randInt(BASE_MIN, BASE_MAX);
        const h = randInt(HAUT_MIN, HAUT_MAX);
        if (((b * h) % 2 === 1) !== veutImpair) continue;
        if (Math.abs(b - h) < 1) continue;   // base et hauteur portent des nombres distincts
        return { b, h };
    }
    return { b: 7, h: 4 };
};

const genFormuleSaisie = () => {
    const { b, h } = tirerSansFigure();
    const [nA, nB, nC] = pickLetters(3);
    const aire = (b * h) / 2;
    const texte = `<p>Dans le triangle ${nA}${nB}${nC}, le côté [${nB}${nC}] mesure ${b} cm et la `
        + `hauteur relative à ce côté mesure ${h} cm. Calcule l'aire de ce triangle.${RAPPEL_UNITE}</p>`;
    return meta(saisieAire(aire, texte, L_FORMULE), 'formule_texte', {
        niveau: 1,
        longueur_de_la_base: b,
        longueur_de_la_hauteur_relative: h,
        parite_du_produit_base_hauteur: (b * h) % 2 === 1 ? 'impair' : 'pair',
        aire,
        erreurVisee: E_UNITE,
    });
};

const genFormuleTrous = () => {
    const { b, h } = tirerSansFigure();
    const produit = b * h;
    const aire = produit / 2;
    const texte = `<p>Un triangle a pour base ${b} cm et pour hauteur relative à cette base ${h} cm. `
        + 'Écris le calcul de son aire en trois temps.</p>';
    // Aucun trou de part et d'autre d'un opérateur commutatif : le produit est
    // écrit en toutes lettres avant d'être demandé.
    const phrase = `Le produit de la base par la hauteur vaut {{${produit}}}. `
        + `En divisant ce produit par 2, on trouve {{${fr(aire)}}}. `
        + `L'aire du triangle vaut donc {{${fr(aire)}}} cm².`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_FORMULE), mode: 'saisie', strict: false,
    });
    return meta(payload, 'formule_texte_trou_saisie', {
        niveau: 1,
        longueur_de_la_base: b,
        longueur_de_la_hauteur_relative: h,
        parite_du_produit_base_hauteur: produit % 2 === 1 ? 'impair' : 'pair',
        aire,
        erreurVisee: E_OUBLI_DIV,
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — directe (niveau 2) : figure, pied intérieur
// ═══════════════════════════════════════════════════════════

const paramsFigure = (t, niveau, extra) => ({
    niveau,
    longueur_de_la_base: t.b,
    longueur_de_la_hauteur_relative: t.h,
    parite_du_produit_base_hauteur: (t.b * t.h) % 2 === 1 ? 'impair' : 'pair',
    position_du_pied_de_la_hauteur: t.p > 0 && t.p < t.b ? 'intérieur au côté' : 'extérieur sur le prolongement',
    distance_du_pied_aux_sommets: Math.min(Math.abs(t.p), Math.abs(t.b - t.p)),
    nombre_de_longueurs_affichees_sur_la_figure: 4,
    longueurs_affichees: [t.b, t.h, t.ab, t.ac],
    aire: (t.b * t.h) / 2,
    ...extra,
});

const figureDe = (t, inclin, nA, nB, nC, options = {}) => figTriangle({
    b: t.b, h: t.h, p: t.p, inclin, nA, nB, nC,
    cotes: {
        base: `${t.b} cm`,
        hauteur: `${t.h} cm`,
        ab: `${t.ab} cm`,
        ac: `${t.ac} cm`,
        ...(options.cotes || {}),
    },
    secondeHauteur: !!options.secondeHauteur,
    s: salt(t.b, t.h, t.p * 100, inclin, randInt(0, 999)),
});

const genDirecteSaisie = () => {
    const t = tirerVivier(VIVIER_INTERIEUR);
    const inclin = tirerInclinaison();
    const [nA, nB, nC] = pickLetters(3);
    const aire = (t.b * t.h) / 2;
    const texte = `<p>Sur cette figure, la hauteur relative au côté [${nB}${nC}] est tracée en `
        + `pointillés et son pied est codé par un angle droit. Quatre longueurs sont données : `
        + `deux seulement servent. Calcule l'aire du triangle ${nA}${nB}${nC}.${RAPPEL_UNITE}</p>`;
    return meta(saisieAire(aire, texte, L_DIRECTE, {
        elements_additionnels: imgElement(figureDe(t, inclin, nA, nB, nC).render()),
    }), 'directe_texte', paramsFigure(t, 2, { inclinaison_de_la_base_sur_la_figure: inclin, erreurVisee: E_UNITE }));
};

const genDirecteTrous = () => {
    const t = tirerVivier(VIVIER_INTERIEUR);
    const inclin = tirerInclinaison();
    const [nA, nB, nC] = pickLetters(3);
    const produit = t.b * t.h;
    const aire = produit / 2;
    const texte = `<p>La hauteur relative au côté [${nB}${nC}] est tracée en pointillés. `
        + 'Écris le calcul de l\'aire en trois temps, en choisissant les deux longueurs utiles.</p>';
    const phrase = `La base utile mesure {{${t.b}}} cm et la hauteur qui lui est relative mesure `
        + `{{${t.h}}} cm. Leur produit vaut {{${produit}}}. L'aire vaut donc {{${fr(aire)}}} cm².`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DIRECTE), mode: 'saisie', strict: false,
        elements_additionnels: imgElement(figureDe(t, inclin, nA, nB, nC).render()),
    });
    return meta(payload, 'directe_texte_trou_saisie',
        paramsFigure(t, 2, { inclinaison_de_la_base_sur_la_figure: inclin, erreurVisee: E_OUBLI_DIV }));
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — obtus (niveau 3) : pied sur le prolongement
// ═══════════════════════════════════════════════════════════

const genObtusSaisie = () => {
    const t = tirerVivier(VIVIER_EXTERIEUR);
    const inclin = tirerInclinaison();
    const [nA, nB, nC] = pickLetters(3);
    const aire = (t.b * t.h) / 2;
    const texte = `<p>Ce triangle a un angle obtus : le pied de la hauteur relative à [${nB}${nC}] `
        + `tombe <b>hors</b> du côté, sur son prolongement (tracé en trait fin). La hauteur reste la `
        + `distance du sommet ${nA} à la droite (${nB}${nC}). Calcule l'aire du triangle `
        + `${nA}${nB}${nC}.${RAPPEL_UNITE}</p>`;
    return meta(saisieAire(aire, texte, L_OBTUS, {
        elements_additionnels: imgElement(figureDe(t, inclin, nA, nB, nC).render()),
    }), 'obtus_texte', paramsFigure(t, 3, {
        inclinaison_de_la_base_sur_la_figure: inclin, erreurVisee: E_HAUTEUR_INT,
    }));
};

const genObtusQcm = () => {
    const t = tirerVivier(VIVIER_EXTERIEUR);
    const inclin = tirerInclinaison();
    const [nA, nB, nC] = pickLetters(3);
    const options = [
        {
            choix: `Le segment en pointillés qui va de ${nA} à la droite (${nB}${nC}), avec le codage d'angle droit à son pied.`,
            code: null,
        },
        { choix: `Le côté [${nA}${nB}], qui relie ${nA} à une extrémité de [${nB}${nC}].`, code: E_COTE_HAUTEUR },
        { choix: 'Le trait le plus vertical de la figure.', code: E_VERTICALE },
        {
            choix: `Aucun : le pied ne tombe pas sur [${nB}${nC}], donc ce triangle n'a pas de hauteur relative à [${nB}${nC}].`,
            code: E_HAUTEUR_INT,
        },
    ];
    const texte = `<p>Sur cette figure, quel segment est la hauteur relative au côté [${nB}${nC}] ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, {
        ...opts(L_OBTUS),
        elements_additionnels: imgElement(figureDe(t, inclin, nA, nB, nC).render()),
    });
    return meta(payload, 'obtus_qcm_unique', paramsFigure(t, 3, {
        inclinaison_de_la_base_sur_la_figure: inclin, distracteurs: options.slice(1),
    }));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — rectangle (niveau 4) : les côtés de l'angle droit
// ═══════════════════════════════════════════════════════════

const genRectangleSaisie = () => {
    const t = randomChoice(VIVIER_RECTANGLE);
    const inclin = tirerInclinaison();
    const [nA, nB, nC] = pickLetters(3);
    const aire = (t.b * t.h) / 2;
    // Le sommet A est au-dessus de B : le pied de la hauteur est B lui-même
    // (exclusion 2, exception explicite du niveau consacré au triangle rectangle).
    const fig = figTriangle({
        b: t.b, h: t.h, p: 0, inclin, nA, nB, nC,
        cotes: { base: `${t.b} cm`, ab: `${t.h} cm`, ac: `${t.hyp} cm`, hauteur: null },
        secondeHauteur: false,
        s: salt(t.b, t.h, inclin, randInt(0, 999)),
    });
    const texte = `<p>Le triangle ${nA}${nB}${nC} est rectangle en ${nB}. Il n'y a pas de formule `
        + `particulière : les deux côtés de l'angle droit, [${nB}${nC}] et [${nA}${nB}], jouent la `
        + `base et la hauteur qui lui est relative. L'hypoténuse ne sert pas. Calcule l'aire du `
        + `triangle ${nA}${nB}${nC}.${RAPPEL_UNITE}</p>`;
    return meta(saisieAire(aire, texte, L_RECT, {
        elements_additionnels: imgElement(fig.render()),
    }), 'rectangle_texte', {
        niveau: 4,
        longueur_de_la_base: t.b,
        longueur_de_la_hauteur_relative: t.h,
        parite_du_produit_base_hauteur: (t.b * t.h) % 2 === 1 ? 'impair' : 'pair',
        position_du_pied_de_la_hauteur: 'confondu avec un sommet',
        inclinaison_de_la_base_sur_la_figure: inclin,
        nombre_de_longueurs_affichees_sur_la_figure: 3,
        longueurs_affichees: [t.b, t.h, t.hyp],
        aire,
        erreurVisee: E_FORMULE_RECT,
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — deux_hauteurs (niveau 5) : choisir le couple
// ═══════════════════════════════════════════════════════════

/** Le vivier intérieur, restreint aux triangles dont les deux pieds sont intérieurs. */
const VIVIER_DEUX_HAUTEURS = VIVIER_INTERIEUR.filter((t) => {
    const A = { x: t.p, y: t.h };
    const B = { x: 0, y: 0 };
    const C = { x: t.b, y: 0 };
    const F = pied(B, A, C);
    return F.t > 0.12 && F.t < 0.88;
});

const genDeuxHauteursSaisie = () => {
    const t = tirerVivier(VIVIER_DEUX_HAUTEURS);
    const inclin = tirerInclinaison();
    const [nA, nB, nC] = pickLetters(3);
    const aire = (t.b * t.h) / 2;
    const texte = `<p>Deux hauteurs sont tracées en pointillés, chacune avec son codage d'angle `
        + `droit : celle qui part de ${nA} (en orange) et celle qui part de ${nB} (en violet). `
        + `Une seule est cotée. Repère le côté sur lequel tombe son pied, puis calcule l'aire du `
        + `triangle ${nA}${nB}${nC}.${RAPPEL_UNITE}</p>`;
    return meta(saisieAire(aire, texte, L_DEUX_H, {
        elements_additionnels: imgElement(figureDe(t, inclin, nA, nB, nC, { secondeHauteur: true }).render()),
    }), 'deux_hauteurs_texte', paramsFigure(t, 5, {
        inclinaison_de_la_base_sur_la_figure: inclin, erreurVisee: E_MAL_ASSOCIEE,
    }));
};

const optionsDeuxHauteurs = (t) => [
    { choix: `${t.b} × ${t.h} ÷ 2`, code: null },
    { choix: `${t.ac} × ${t.h} ÷ 2`, code: E_MAL_ASSOCIEE },
    { choix: `${t.ab} × ${t.ac} ÷ 2`, code: E_DEUX_COTES },
    { choix: `${t.b} × ${t.h}`, code: E_OUBLI_DIV },
];

const distincts = (options) => new Set(options.map((o) => o.choix)).size === options.length;

const genDeuxHauteursQcm = () => {
    // Quatre propositions, nombre CONSTANT : un tirage où deux calculs s'écrivent
    // pareil (le côté inutile vaut la base) est rejoué en entier, jamais complété
    // par un bouche-trou. Repli déterministe : le premier triangle du vivier qui
    // donne quatre écritures distinctes.
    let t = tirerVivier(VIVIER_DEUX_HAUTEURS);
    for (let essai = 0; essai < 40 && !distincts(optionsDeuxHauteurs(t)); essai++) {
        t = tirerVivier(VIVIER_DEUX_HAUTEURS);
    }
    if (!distincts(optionsDeuxHauteurs(t))) {
        t = VIVIER_DEUX_HAUTEURS.find((c) => distincts(optionsDeuxHauteurs(c)));
    }
    const options = optionsDeuxHauteurs(t);
    const inclin = tirerInclinaison();
    const [nA, nB, nC] = pickLetters(3);
    const texte = `<p>Deux hauteurs sont tracées, une seule est cotée : elle part de ${nA} et son `
        + `pied tombe sur [${nB}${nC}]. Quel calcul donne l'aire du triangle ${nA}${nB}${nC}, en cm² ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, {
        ...opts(L_DEUX_H),
        elements_additionnels: imgElement(figureDe(t, inclin, nA, nB, nC, { secondeHauteur: true }).render()),
    });
    return meta(payload, 'deux_hauteurs_qcm_unique', paramsFigure(t, 5, {
        inclinaison_de_la_base_sur_la_figure: inclin, distracteurs: options.slice(1),
    }));
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    formule_texte: genFormuleSaisie,
    formule_texte_trou_saisie: genFormuleTrous,
    directe_texte: genDirecteSaisie,
    directe_texte_trou_saisie: genDirecteTrous,
    obtus_texte: genObtusSaisie,
    obtus_qcm_unique: genObtusQcm,
    rectangle_texte: genRectangleSaisie,
    deux_hauteurs_texte: genDeuxHauteursSaisie,
    deux_hauteurs_qcm_unique: genDeuxHauteursQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'formule', label: L_FORMULE, formatsList: buildFormats('formule') },
    { id: 'directe', label: L_DIRECTE, formatsList: buildFormats('directe') },
    { id: 'obtus', label: L_OBTUS, formatsList: buildFormats('obtus') },
    { id: 'rectangle', label: L_RECT, formatsList: buildFormats('rectangle') },
    { id: 'deux_hauteurs', label: L_DEUX_H, formatsList: buildFormats('deux_hauteurs') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    VIVIER_INTERIEUR, VIVIER_EXTERIEUR, VIVIER_RECTANGLE, VIVIER_DEUX_HAUTEURS,
    ecrituresAire, tirerSansFigure, tirerVivier, tirerInclinaison,
};
